import * as _ from 'underscore';
import { getAppStore } from '../../app/AppStore';
import { SetOwnerDialogBody, SetOwnerDialogBodyProps, SetOwnerDialogState } from '../../app/content/SetOwnerDialog';
import { CompositeSessionInfoSelectors, SessionUserSelectors } from '../../app/selectors/SessionInfoSelectors';
import { UpdateCollectionsResponseData } from '../../app/spaces/Collections/CollectionModels';
import { CollectionsResourceQuery, UpdateCollectionData } from '../../app/spaces/Collections/Collections';
import { ICollectionInfo } from '../../app/spaces/Collections/ICollectionInfo';
import { Formatters, Messages } from '../../codegen/Localize';
import { ActionHelpers } from '../../commonInfra/contentActions/ActionHelpers';
import { openReactActionDialog } from '../../commonInfra/dialogs/ReactActionDialog';
import { TabDialogWidth } from '../../commonInfra/dialogs/style/TabDialogWidth';
import { toQPromise } from '../../commonInfra/utils/QUtils';
import { DataSourceErrors } from '../../content/services/DataSourceErrors';
import { ProjectErrors } from '../../content/services/ProjectErrors';
import { DataAlerts } from '../../dda/resources/DataAlerts';
import { FlowErrors } from '../../flow/services/FlowErrors';
import { IMetricInfo } from '../../metrics/models/IMetricInfo';
import { Metrics } from '../../metrics/resources/Metrics';
import { MetricErrors } from '../../metrics/services/MetricErrors';
import { Projects } from '../../projects/resources/Projects';
import { PublishedConnections } from '../../publishedConnection/resources/PublishedConnections';
import { IActionContext } from '../helpers/ActionContext';
import { ResultHelper } from '../../httpClient/utils/ResultHelper';
import { L10n } from '../L10n/L10n';
import { CollectionMapper } from '../mappers/CollectionMapper';
import { FlowListItemInfo } from '../models/FlowInfo';
import { IContentInfo } from '../models/IContentInfo';
import { IDataAlertBase } from '../models/IDataAlertInfo';
import { IDataRoleInfo } from '../models/IDataRoleInfo';
import { IDataSourceBase } from '../models/IDataSourceInfo';
import { ILensInfo } from '../models/ILensInfo';
import { IProjectBase } from '../models/IProjectInfo';
import { IPublishedConnectionInfo } from '../models/IPublishedConnectionInfo';
import { IWorkbookBase } from '../models/IWorkbookInfo';
import { IErrorItem } from '../../commonInfra/contentActions/MultipleItemResult';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { DataRoles } from '../resources/Dataroles';
import { DataSources } from '../../content/resources/Datasources';
import { Flows } from '../resources/Flows';
import { Lenses } from '../resources/Lenses';
import { Workbooks } from '../../content/resources/Workbooks';
import { CustomViews } from '../ServerApi/CustomViews/CustomViews';
import { ICustomViewBase, ICustomViewInfo } from '../ServerApi/CustomViews/ICustomViewInfo';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { IUserName } from '../ServerApi/types/IUser';
import { CollectionErrors } from './CollectionErrors';
import {
  standardActionErrorMsg,
  StandardActionToastMessages
} from '../../commonInfra/contentActions/ActionHelpers';
import { CustomViewErrors } from './customViews/CustomViewErrors';
import { DataAlertErrors } from './DataAlertErrors';
import { DataRoleErrors } from './DataRoleErrors';
import { LensErrors } from './LensErrors';
import { PublishedConnectionErrors } from './PublishedConnectionErrors';
import { SearchSuggestionsService } from './SearchSuggestionsService';
import { WorkbookErrors } from './WorkbookErrors';


export class ChangeContentOwnerAction {
  public static $inject = [
    '$q',
    'SearchSuggestionsService',
    'WorkbookErrors',
    'DataAlertErrors',
  ];

  constructor(
    private $q: ng.IQService,
    private SearchSuggestionsService: SearchSuggestionsService,
    private WorkbookErrors: WorkbookErrors,
    private DataAlertErrors: DataAlertErrors,
  ) {}

  public confirmChangeOwner(content: IContentInfo[], _context: IActionContext): ng.IPromise<IResult> {
    const isAdmin = CompositeSessionInfoSelectors.currentUserIsSiteOrServerAdminSelector(getAppStore().getState());
    const userId = SessionUserSelectors.sessionUserId(getAppStore().getState());
    const isOwner = _.some(content, (item) => item.owner && item.owner.id === userId);
    const containsOneWorkbook = !_.isUndefined(content.find((info) => info.contentType === 'workbook'));
    const count = content.length;
    const modalInstance = openReactActionDialog<SetOwnerDialogBodyProps, SetOwnerDialogState, IResult>({
      title:  Messages.changeOwnerDialogTitle(),
      bodyComponent: SetOwnerDialogBody,
      initialBodyProps: {
        prompt: getPromptForContent(content),
        showCredentialsWarning: containsOneWorkbook,
        isAdmin,
        isOwner,
        getQuery: (matchText: string) => this.getQuery(content, matchText)
      },
      initialDialogState: { targetOwner: null },
      actionName: Messages.changeOwnerConfirm({ COUNT: count, COUNT_LOC: Formatters.formatCount(count) }),
      action: (_bodyProps: SetOwnerDialogBodyProps, dialogState: SetOwnerDialogState) => {
        return this.setOwnerForContent(content, dialogState.targetOwner).then((results) => {
          return ResultHelper.combineResults(results);
        });
      },
      actionDisabled: (_bodyProps: SetOwnerDialogBodyProps, dialogState: SetOwnerDialogState) => dialogState.targetOwner == null,
      dialogWidth: TabDialogWidth.width500px,
      dismissDialogOnActionFailed: false,
    });

    return modalInstance.result;
  }

  private getQuery(content: IContentInfo[], matchText: string) {
    const containsProjects = content.find(i => i.contentType === 'project') !== undefined;
    if (containsProjects) {
      return this.SearchSuggestionsService.usersCanOwnProject(matchText);
    }
    return this.SearchSuggestionsService.usersWithoutGuest(matchText);
  }

  // public for testing only!  Calling this bypasses the confirm dialog UI.
  public setOwnerForContent(content: IContentInfo[], owner: IUserName) {
    const projects = Set.fromIdItemArray(content.filter(i => i.contentType === 'project'));
    const workbooks = Set.fromIdItemArray(content.filter(i => i.contentType === 'workbook'));
    const datasources = Set.fromIdItemArray(content.filter(i => i.contentType === 'datasource'));
    const flows = Set.fromIdItemArray(content.filter(i => i.contentType === 'flow'));
    const dataroles = Set.fromIdItemArray(content.filter(i => i.contentType === 'dataRole'));
    const lenses = Set.fromIdItemArray(content.filter(i => i.contentType === 'lens'));
    const alerts = Set.fromIdItemArray(content.filter(i => i.contentType === 'dataalert'));
    const metrics = Set.fromIdItemArray(content.filter(i => i.contentType === 'metric'));
    const publishedConnections = Set.fromIdItemArray(content.filter(i => i.contentType === 'publishedConnection'));
    const collections = Set.fromIdItemArray(content.filter(i => i.contentType === 'collection'));
    const customViews = Set.fromIdItemArray(content.filter(i => i.contentType === 'customView'));

    return this.$q.all([
      this.setOwnerForProjects(projects, owner),
      this.setOwnerForWorkbooks(workbooks, owner),
      this.setOwnerForDatasources(datasources, owner),
      this.setOwnerForFlows(flows, owner),
      this.setOwnerForDataRoles(dataroles, owner),
      this.setOwnerForLenses(lenses, owner),
      this.setOwnerForDataAlerts(alerts, owner),
      this.setOwnerForMetrics(metrics, owner),
      this.setOwnerForPublishedConnections(publishedConnections, owner),
      this.setOwnerForCollections(collections, owner),
      this.setOwnerForCustomViews(customViews, owner),
    ]);
  }

  private setOwnerForProjects(projects: Set<IContentInfo>, owner: IUserName): ng.IPromise<IResult> {
    if (projects.size() < 1) {
      return this.$q.when(null);
    }

    return Projects.setOwner(projects.keys(), owner.id).then((result: IResult) => {
      return ActionHelpers.displayStandardToastsForResult(
        projects,
        result,
        {
          success: changeOwnerSuccessTranslation(Messages.changeOwnerProjectsSuccess, owner),
          error: standardActionErrorMsg(Messages.changeOwnerProjectsError),
        },
        (errorItem: IErrorItem<IProjectBase>) => ProjectErrors.errorDetailsFor(errorItem),
      );
    });
  }

  private setOwnerForMetrics(metrics: Set<IContentInfo>, owner: IUserName): ng.IPromise<IResult> {
    if (metrics.size() < 1) {
      return this.$q.when(null);
    }

    return Metrics.setOwner(metrics.keys(), owner.id).then((result: IResult) => {
      return ActionHelpers.displayStandardToastsForResult(
        metrics,
        result,
        {
          success: changeOwnerSuccessTranslation(Messages.changeOwnerForMetricsSuccess, owner),
          error: standardActionErrorMsg(Messages.changeOwnerForMetricsError),
         },
        (errorItem: IErrorItem<IMetricInfo>) => MetricErrors.errorDetailsFor(errorItem),
      );
    });
  }

  private setOwnerForPublishedConnections(publishedConnections: Set<IContentInfo>, owner: IUserName): ng.IPromise<IResult> {
    if (publishedConnections.size() < 1) {
      return this.$q.when(null);
    }

    return PublishedConnections.setOwner(publishedConnections.keys(), owner.id).then((result: IResult) => {
      return ActionHelpers.displayStandardToastsForResult(
        publishedConnections,
        result,
        {
          success: changeOwnerSuccessTranslation(Messages.changeOwnerPublishedConnectionsSuccess, owner),
          error: standardActionErrorMsg(Messages.changeOwnerPublishedConnectionsError),
        },
        (errorItem: IErrorItem<IPublishedConnectionInfo>) => PublishedConnectionErrors.errorDetailsFor(errorItem),
      );
    });
  }

  private setOwnerForWorkbooks(workbooks: Set<IContentInfo>, owner: IUserName): Promise<IResult> {
    if (workbooks.size() < 1) {
      return Promise.resolve(null);
    }

    return Workbooks.setOwner(workbooks.keys(), owner.id).then((result: IResult) => {
      return ActionHelpers.displayStandardToastsForResult(
        workbooks,
        result,
        {
          success: changeOwnerSuccessTranslation(Messages.changeOwnerWorkbooksSuccess, owner),
          error: standardActionErrorMsg(Messages.changeOwnerWorkbooksError),
        },
        (errorItem: IErrorItem<IWorkbookBase>) => this.WorkbookErrors.errorDetailsFor(errorItem),
      );
    });
  }

  private setOwnerForDatasources(datasources: Set<IContentInfo>, owner: IUserName): Promise<IResult> {
    if (datasources.size() < 1) {
      return Promise.resolve(null);
    }

    return DataSources.setOwner(datasources.keys(), owner.id).then((result: IResult) => {
      return ActionHelpers.displayStandardToastsForResult(
        datasources,
        result,
        {
          success: changeOwnerSuccessTranslation(Messages.changeOwnerDatasourcesSuccess, owner),
          error: standardActionErrorMsg(Messages.changeOwnerDatasourcesError),
        },
        (errorItem: IErrorItem<IDataSourceBase>) => DataSourceErrors.errorDetailsFor(errorItem),
      );
    });
  }

  private setOwnerForFlows(flows: Set<IContentInfo>, owner: IUserName): ng.IPromise<IResult> {
    if (flows.size() < 1) {
      return this.$q.when(null);
    }

    return Flows.setOwner(flows.keys(), owner.id).then((result: IResult) => {
      return ActionHelpers.displayStandardToastsForResult(
        flows,
        result,
        {
          success: changeOwnerSuccessTranslation(Messages.changeOwnerFlowsSuccess, owner),
          error: standardActionErrorMsg(Messages.changeOwnerFlowsError),
        },
        (errorItem: IErrorItem<FlowListItemInfo>) => FlowErrors.errorDetailsFor(errorItem),
      );
    });
  }

  private setOwnerForDataRoles(dataroles: Set<IContentInfo>, owner: IUserName): ng.IPromise<IResult> {
    if (dataroles.size() < 1) {
      return this.$q.when(null);
    }

    return DataRoles.setOwner(dataroles.keys(), owner.id).then((result: IResult) => {
      return ActionHelpers.displayStandardToastsForResult(
        dataroles,
        result,
        {
          success: changeOwnerSuccessTranslation(Messages.changeOwnerDataRolesSuccess, owner),
          error: standardActionErrorMsg(Messages.changeOwnerDataRolesError),
        },
        (errorItem: IErrorItem<IDataRoleInfo>) => DataRoleErrors.errorDetailsFor(errorItem),
      );
    });
  }

  private setOwnerForLenses(lenses: Set<IContentInfo>, owner: IUserName): ng.IPromise<IResult> {
    if (lenses.size() < 1) {
      return this.$q.when(null);
    }

    return Lenses.setOwner(lenses.keys(), owner.id).then((result: IResult) => {
      return ActionHelpers.displayStandardToastsForResult(
        lenses,
        result,
        {
          success: changeOwnerSuccessTranslation(Messages.changeOwnerLensesSuccess, owner),
          error: standardActionErrorMsg(Messages.changeOwnerLensesError),
        },
        (errorItem: IErrorItem<ILensInfo>) => LensErrors.errorDetailsFor(errorItem),
      );
    });
  }

  private setOwnerForDataAlerts(alerts: Set<IContentInfo>, owner: IUserName): ng.IPromise<IResult> {
    if (alerts.size() < 1) {
      return this.$q.when(null);
    }

    return toQPromise(DataAlerts.setOwner(alerts.keys(), owner.id).then((result: IResult) => {
      return ActionHelpers.displayStandardToastsForResult<IDataAlertBase>(
        alerts as Set<IDataAlertBase>,
        result,
        {
          success: changeOwnerSuccessTranslation(Messages.changeOwnerDataAlertsSuccess, owner),
          error: standardActionErrorMsg(Messages.changeOwnerDataAlertsError),
        },
        (errorItem: IErrorItem<IDataAlertBase>) => this.DataAlertErrors.errorDetailFor(errorItem),
      );
    }));
  }

  private setOwnerForCollections(collections: Set<IContentInfo>, owner: IUserName): ng.IPromise<IResult> {
    if (collections.size() < 1) {
      return this.$q.when(null);
    }

    const updateModels: UpdateCollectionData[] = collections.map((collection) => {
      return {
        luid: collection.luid,
        ownerLuid: owner.luid,
      };
    });

    const promise =
      new CollectionsResourceQuery().update(updateModels).then(
        (result: UpdateCollectionsResponseData): IResult => {
          return CollectionMapper.mapUpdateCollectionsResponseDataToIResult(result, collections);
      }).catch((error: any)=> {
        return {errors: [{code: error.data.errorCode, id: undefined}]} as IResult;
      });
    return promise.then((result: IResult) => {
      return ActionHelpers.displayStandardToastsForResult<ICollectionInfo>(
        collections as Set<ICollectionInfo>,
        result,
        {
          success: changeOwnerSuccessTranslation(Messages.CollectionsChangeOwnerSuccess, owner),
          error: standardActionErrorMsg(Messages.CollectionsChangeOwnerError),
        },
        (errorItem: IErrorItem<ICollectionInfo>) => CollectionErrors.errorDetailsFor(errorItem)
      );
    });
  }

  private setOwnerForCustomViews(customViews: Set<IContentInfo>, owner: IUserName): Promise<IResult> {
    if (customViews.size() < 1) {
      return Promise.resolve(null);
    }

    return CustomViews.setOwner(customViews.keys(), owner.id).then((result: IResult) => {
      return ActionHelpers.displayStandardToastsForResult<ICustomViewBase>(
        customViews as Set<ICustomViewBase>,
        result,
        {
          success: changeOwnerSuccessTranslation(Messages.changeOwnerCustomViewsSuccess, owner),
          error: standardActionErrorMsg(Messages.changeOwnerCustomViewsError),
        },
        (errorItem: IErrorItem<ICustomViewInfo>) => CustomViewErrors.errorDetailsFor(errorItem),
      );
    });
  }
}

export function changeOwnerSuccessTranslation(
  messagesFn: (a: { ITEM_COUNT: number, ITEM_COUNT_LOC: string, OWNER_NAME: string }) => string,
  owner: IUserName,
): StandardActionToastMessages<unknown>['success'] {
  return ({ successCount }) => messagesFn({
    ITEM_COUNT: successCount,
    ITEM_COUNT_LOC: Formatters.formatCount(successCount),
    OWNER_NAME: owner.displayName,
  });
}


function getPromptForContent(content: IContentInfo[]): string {
  if (content.length === 1) {
    const item = content[0];
    return Messages.changeOwnerContentItemPrompt({ NAME: L10n.elideIfLong(item.name) });
  } else {
    return Messages.changeOwnerContentItemsPrompt({ COUNT_LOC: Formatters.formatCount(content.length) });
  }
}

Module.instance().service('ChangeContentOwnerAction', ChangeContentOwnerAction);
